import React, { useState, useEffect, useMemo } from 'react';
import SEO from "../../common/SEO";
import Layout from "../../common/Layout";
import { useParams } from "react-router-dom";
import ProductsData from "../../data/products/ProductsData.json";
import '../../assets/scss/elements/_product-detail.scss';
import '../../assets/scss/elements/_product-detail-breadcrumbs.scss';

// Flatten a product's children tree into a single list of nodes
const flattenNodes = (productNode) => {
    let result = [];
    (productNode.children || []).forEach(child => {
        result.push(child);
        result = result.concat(flattenNodes(child));
    });
    return result;
};

const Products = () => {
    const { id } = useParams();
    const productId = id ? parseInt(id, 10) : 1;

    // Get nodeId from query params if navigating from the product tree
    const searchParams = new URLSearchParams(window.location.search);
    const nodeId = searchParams.get('nodeId');

    // Resolve the base product and, if navigating from the product tree, the matching node
    const baseProduct = useMemo(() => ProductsData.find(p => p.id === productId) || ProductsData[0], [productId]);
    const node = useMemo(() => {
        if (!nodeId) return null;
        return flattenNodes(baseProduct).find(n => n.id === parseInt(nodeId, 10)) || null;
    }, [baseProduct, nodeId]);

    // Merge node data with product data for display
    const productData = useMemo(() => node ? {
        ...baseProduct,
        title: node.title || baseProduct.title,
        itemNo: node.itemNo || baseProduct.itemNo,
        type: node.type || baseProduct.type,
        ean: node.ean || baseProduct.ean,
        images: (node.images && node.images.length) ? node.images : baseProduct.images || [],
        specifications: (node.specifications && Object.keys(node.specifications).length) ? node.specifications : baseProduct.specifications || [],
        dimensions: (node.dimensions && Object.keys(node.dimensions).length) ? node.dimensions : baseProduct.dimensions || [],
        technicalData: (node.technicalData && Object.keys(node.technicalData).length) ? node.technicalData : baseProduct.technicalData || [],
        loads: (node.loads && node.loads.length) ? node.loads : baseProduct.loads || [],
        variants: (node.variants && node.variants.length) ? node.variants : baseProduct.variants || [],
        customFields: (node.customFields && node.customFields.length) ? node.customFields : baseProduct.customFields || [],
        downloads: Object.values(node.downloads || {}).some(items => items && items.length) ? node.downloads : baseProduct.downloads || {},
        // Only use node's description, don't fallback to parent product description
        description: (node.description && node.description.trim()) || null,
        sellingUnit: node.sellingUnit || baseProduct.sellingUnit || '',
        datasheetUrl: node.datasheetUrl || baseProduct.datasheetUrl || '',
        variantColumns: node.variantColumns || [],
        // SEO fields - use node's SEO if available, otherwise fallback to product's SEO
        metaTitle: node.metaTitle || baseProduct.metaTitle || null,
        metaDescription: node.metaDescription || baseProduct.metaDescription || null
    } : {
        ...baseProduct,
        metaTitle: baseProduct.metaTitle || null,
        metaDescription: baseProduct.metaDescription || null
    }, [baseProduct, node]);

    const [activeTab, setActiveTab] = useState('description');
    // quantity used in quantity input (value={quantity}) further down in render
    // eslint-disable-next-line no-unused-vars
    const [quantity, setQuantity] = useState(3);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [openDownloadCategory, setOpenDownloadCategory] = useState(null);
    const [selectedDownloads, setSelectedDownloads] = useState([]);
    // selectedItem used in dropdowns and sync effect; setter used in handleSelect
    // eslint-disable-next-line no-unused-vars
    const [selectedItem, setSelectedItem] = useState({
        itemNo: '',
        type: '',
        length: '',
        width: '',
        thickness: ''
    });
    const [selectedCustomFields, setSelectedCustomFields] = useState({});
    const [imageErrors, setImageErrors] = useState({});

    // Use same-origin on live so product images and downloads load
    const ASSET_BASE = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
      ? window.location.origin
      : (process.env.REACT_APP_API_URL || 'http://localhost:3005').replace(/\/api\/?$/, '').replace(/\/+$/, '');

    // Update selectedItem when productData changes
    useEffect(() => {
        if (productData) {
            // Handle both array format (new) and object format (legacy) for specifications
            let specs = productData.specifications || {};
            if (Array.isArray(specs)) {
                specs = specs.reduce((acc, spec) => {
                    if (spec.fieldName && spec.fieldValue) {
                        acc[spec.fieldName] = spec.fieldValue;
                    }
                    return acc;
                }, {});
            }
            
            setSelectedItem({
                itemNo: productData.itemNo || node?.itemNo || '',
                type: productData.type || node?.type || '',
                length: specs.length || '',
                width: specs.width || '',
                thickness: specs.plateThickness || ''
            });
            setQuantity(parseInt(productData.sellingUnit) || 3);
            
            // Initialize custom fields selections (only for fields with multiple options)
            const customFields = productData.customFields || [];
            const initialCustomFields = {};
            customFields.forEach(field => {
                // Only initialize selection state for fields with multiple options
                // Single option fields don't need selection state
                if (field.fieldName && field.dropdownOptions && field.dropdownOptions.length > 1) {
                    initialCustomFields[field.fieldName] = field.dropdownOptions[0] || '';
                }
            });
            setSelectedCustomFields(initialCustomFields);
        }
    }, [productData, node]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside dropdown wrapper and dropdown modal
            if (!event.target.closest('.dropdown-wrapper') && 
                !event.target.closest('.dropdown-modal') &&
                !event.target.closest('.dropdown-option')) {
                setOpenDropdown(null);
            }
        };
        if (openDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDropdown]);

    // Handle both array format (new) and object format (legacy) for specifications, dimensions, technicalData
    const specsArray = productData ? (Array.isArray(productData.specifications) ? productData.specifications : 
        (productData.specifications ? Object.entries(productData.specifications).map(([key, value]) => ({
            fieldName: key,
            fieldLabel: key,
            fieldValue: value
        })) : [])) : [];
    const dimsArray = productData ? (Array.isArray(productData.dimensions) ? productData.dimensions : 
        (productData.dimensions ? Object.entries(productData.dimensions).map(([key, value]) => ({
            fieldName: key,
            fieldLabel: key,
            fieldValue: value
        })) : [])) : [];
    const techArray = productData ? (Array.isArray(productData.technicalData) ? productData.technicalData : 
        (productData.technicalData ? Object.entries(productData.technicalData).map(([key, value]) => ({
            fieldName: key,
            fieldLabel: key,
            fieldValue: value
        })) : [])) : [];
    
    const variants = productData?.variants || [];
    const downloadItems = productData?.downloads || {};
    const customFields = productData?.customFields || [];

    // Check if each section has data
    // Description: must exist, be a string, and have meaningful content after trimming whitespace
    const descriptionValue = productData?.description;
    const hasDescription = descriptionValue !== null && 
        descriptionValue !== undefined && 
        descriptionValue !== '' &&
        typeof descriptionValue === 'string' && 
        descriptionValue.trim().length > 0;
    const hasTechnicalData = dimsArray.length > 0 || techArray.length > 0 || (productData?.loads && productData.loads.length > 0);
    const hasDownloads = downloadItems && Object.keys(downloadItems).some(key => 
        downloadItems[key] && Array.isArray(downloadItems[key]) && downloadItems[key].length > 0
    );
    const hasVariants = variants && variants.length > 0;

    // Build tabs array only for sections with data
    const availableTabs = [];
    if (hasDescription) {
        availableTabs.push({ id: 'description', label: 'Description' });
    }
    if (hasTechnicalData) {
        availableTabs.push({ id: 'technical', label: 'Technical data' });
    }
    if (hasDownloads) {
        availableTabs.push({ id: 'downloads', label: 'Downloads' });
    }
    if (hasVariants) {
        availableTabs.push({ id: 'variants', label: 'Variants' });
    }

    // If activeTab is not in available tabs, set it to the first available tab
    // This hook must be called before any early returns
    useEffect(() => {
        if (availableTabs.length > 0) {
            const tabExists = availableTabs.find(tab => tab.id === activeTab);
            if (!tabExists) {
                setActiveTab(availableTabs[0].id);
            }
        }
    // Intended: sync activeTab when available tabs change. availableTabs is derived from has* flags.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasDescription, hasTechnicalData, hasDownloads, hasVariants, activeTab]);

    // Use filtered tabs
    const filteredTabs = availableTabs;

    // Show fallback state if no product data
    if (!productData) {
        return (
            <Layout>
                <div style={{ padding: '50px', textAlign: 'center' }}>
                    <p>Product not found</p>
                </div>
            </Layout>
        );
    }

    // Dropdown options (used in commented-out specification dropdowns; keep for when re-enabled)
    // eslint-disable-next-line no-unused-vars
    const itemNoOptions = productData.dropdownOptions?.itemNoType ||
        (variants.length > 0 ? variants.map(v => ({ itemNo: v.itemNo || '', type: v.type || '' })) :
        [{ itemNo: productData.itemNo || node?.itemNo || '', type: productData.type || node?.type || '' }]);

    const lengthSpec = specsArray.find(s => s.fieldName === 'length' || s.fieldLabel === 'Length');
    // eslint-disable-next-line no-unused-vars
    const lengthOptions = Array.isArray(productData.dropdownOptions?.length)
        ? productData.dropdownOptions.length
        : (lengthSpec?.fieldValue ? [lengthSpec.fieldValue] : ['3000', '2000', '1500', '1000']);

    const widthSpec = specsArray.find(s => s.fieldName === 'width' || s.fieldLabel === 'Width');
    // eslint-disable-next-line no-unused-vars
    const widthOptions = Array.isArray(productData.dropdownOptions?.width)
        ? productData.dropdownOptions.width
        : (widthSpec?.fieldValue ? [widthSpec.fieldValue] : ['50', '100', '150', '200', '300', '400', '500']);

    const thicknessSpec = specsArray.find(s => s.fieldName === 'plateThickness' || s.fieldLabel === 'Plate Thickness');
    // eslint-disable-next-line no-unused-vars
    const thicknessOptions = Array.isArray(productData.dropdownOptions?.thickness)
        ? productData.dropdownOptions.thickness
        : (thicknessSpec?.fieldValue ? [thicknessSpec.fieldValue] : ['0.8', '1.0', '1.2', '1.5']);

    const handleDropdownToggle = (dropdownName, e) => {
        e?.stopPropagation();
        setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
    };

    // Used by commented-out item/length/width/thickness dropdowns
    // eslint-disable-next-line no-unused-vars
    const handleSelect = (dropdownName, value, additionalData = {}, e) => {
        e?.stopPropagation();
        e?.preventDefault();
        setSelectedItem(prev => ({
            ...prev,
            [dropdownName]: value,
            ...additionalData
        }));
        setOpenDropdown(null);
    };

    const handleCustomFieldSelect = (fieldName, value, e) => {
        e?.stopPropagation();
        e?.preventDefault();
        setSelectedCustomFields(prev => ({
            ...prev,
            [fieldName]: value
        }));
        setOpenDropdown(null);
    };

    const handleDownloadCategoryToggle = (category) => {
        setOpenDownloadCategory(openDownloadCategory === category ? null : category);
    };

    const handleDownloadToggle = (category, index) => {
        const downloadId = `${category}-${index}`;
        setSelectedDownloads(prev => 
            prev.includes(downloadId) 
                ? prev.filter(id => id !== downloadId)
                : [...prev, downloadId]
        );
    };

    const handleDownloadSelection = () => {
        // Download all selected files
        selectedDownloads.forEach(downloadId => {
            const [category, index] = downloadId.split('-');
            const download = downloadItems[category]?.[parseInt(index)];
            if (download && download.url) {
                const downloadUrl = download.url.startsWith('/uploads/') 
                    ? `${ASSET_BASE}${download.url}`
                    : download.url;
                window.open(downloadUrl, '_blank');
            }
        });
    };

    // Handle image errors
    const handleImageError = (imageIndex) => {
        setImageErrors(prev => ({
            ...prev,
            [imageIndex]: true
        }));
    };

    // Placeholder image URL - Updated to match new image sizes (300px height)
    const placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';

    return (
        <>
            <SEO 
                title={productData.title} 
                metaTitle={productData.metaTitle} 
                metaDescription={productData.metaDescription} 
            />
            <Layout>
                <div className="product-detail-page">

                    <div className="main-content">
                        <div className="container">
                            {/* Product Title Section */}
                            <div className="product-title-section">
                                <h1 className="product-title">{productData.title}</h1>
                                <div className="product-info">
                                    <span>Type {productData.type || node?.type || '-'}</span> | <span>Item no. {productData.itemNo || node?.itemNo || '-'}</span> | <span>EAN {productData.ean || '-'}</span>
                                </div>
                            </div>

                            {/* Product Media and Summary Section */}
                            <div className="row product-main-section">
                                {/* Left: Product Media */}
                                <div className="col-lg-7 col-md-6">
                                    <div className="product-media">
                                        <div className="product-images-grid">
                                            {Array.isArray(productData.images) && productData.images.map((image, index) => {
                                                // Handle both relative paths and full URLs
                                                const resolvedImage = typeof image === 'string' ? { url: image, alt: '' } : {
                                                    url: image?.url || image?.path || '',
                                                    alt: image?.alt || ''
                                                };
                                                const rawPath = resolvedImage.url || '';
                                                let imagePath = '';
                                                if (rawPath) {
                                                    if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) {
                                                        imagePath = rawPath;
                                                    } else if (rawPath.startsWith('/uploads/')) {
                                                        imagePath = `${ASSET_BASE}${rawPath}`;
                                                    } else if (rawPath) {
                                                        // Assume it's a relative path without leading slash
                                                        imagePath = `${ASSET_BASE}/uploads/product/${rawPath}`;
                                                    }
                                                }
                                                return (
                                                    <div key={index} className="product-image-item">
                                                        <img 
                                                            src={imageErrors[`image-${index}`] || !imagePath ? placeholderImage : imagePath} 
                                                            alt={resolvedImage.alt || productData.title} 
                                                            onError={() => handleImageError(`image-${index}`)}
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {productData.datasheetUrl && (
                                            <div className="product-download-link">
                                                <a 
                                                    href={productData.datasheetUrl.startsWith('http') || productData.datasheetUrl.startsWith('//') 
                                                        ? productData.datasheetUrl 
                                                        : productData.datasheetUrl.startsWith('/uploads/')
                                                        ? `${ASSET_BASE}${productData.datasheetUrl}`
                                                        : productData.datasheetUrl} 
                                                    className="download-link"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <span className="download-icon">⬇</span>
                                                    Data sheet (PDF)
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right: Product Summary */}
                                <div className="col-lg-5 col-md-6">
                                    <div className="product-summary-card">
                                        <div className="summary-header">
                                            <a href="/about" className="show-variants-link">Specifications</a>
                                        </div>
                                        
                                        <div className="product-details-list">
                                            {/* Item no. / Type - Commented out */}
                                            {/* <div className="detail-item">
                                                <div className="detail-label">
                                                    Item no. / Type
                                                </div>
                                                <div 
                                                    className="detail-value dropdown-wrapper"
                                                    onClick={(e) => handleDropdownToggle('itemNo', e)}
                                                    style={{ cursor: 'pointer', position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                >
                                                    <span>{selectedItem.itemNo || 'Select'} / {selectedItem.type || 'Select'}</span>
                                                    <span 
                                                        className={`dropdown-arrow ${openDropdown === 'itemNo' ? 'open' : ''}`}
                                                    ></span>
                                                    {openDropdown === 'itemNo' && (
                                                        <div className="dropdown-modal">
                                                            <div className="dropdown-options">
                                                                {itemNoOptions && itemNoOptions.length > 0 ? (
                                                                    itemNoOptions.map((option, index) => (
                                                                        <div 
                                                                            key={index}
                                                                            className="dropdown-option"
                                                                            onClick={(e) => handleSelect('itemNo', option.itemNo || '', { type: option.type || '' }, e)}
                                                                        >
                                                                            {option.itemNo || ''} / {option.type || ''}
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <div className="dropdown-option" style={{ color: 'var(--color-body)', cursor: 'default' }}>
                                                                        No options available
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div> */}
                                            
                                            {/* Length - Commented out */}
                                            {/* <div className="detail-item">
                                                <div className="detail-label">
                                                    Length
                                                </div>
                                                <div 
                                                    className="detail-value dropdown-wrapper"
                                                    onClick={(e) => handleDropdownToggle('length', e)}
                                                    style={{ cursor: 'pointer', position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                >
                                                    <span>{selectedItem.length || 'Select'} mm</span>
                                                    <span 
                                                        className={`dropdown-arrow ${openDropdown === 'length' ? 'open' : ''}`}
                                                    ></span>
                                                    {openDropdown === 'length' && (
                                                        <div className="dropdown-modal">
                                                            <div className="dropdown-options">
                                                                {lengthOptions && lengthOptions.length > 0 ? (
                                                                    lengthOptions.map((option, index) => (
                                                                        <div 
                                                                            key={index}
                                                                            className="dropdown-option"
                                                                            onClick={(e) => handleSelect('length', option, {}, e)}
                                                                        >
                                                                            {option} mm
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <div className="dropdown-option" style={{ color: 'var(--color-body)', cursor: 'default' }}>
                                                                        No options available
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div> */}
                                            
                                            {/* Width - Commented out */}
                                            {/* <div className="detail-item">
                                                <div className="detail-label">
                                                    Width
                                                </div>
                                                <div 
                                                    className="detail-value dropdown-wrapper"
                                                    onClick={(e) => handleDropdownToggle('width', e)}
                                                    style={{ cursor: 'pointer', position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                >
                                                    <span>{selectedItem.width || 'Select'} mm</span>
                                                    <span 
                                                        className={`dropdown-arrow ${openDropdown === 'width' ? 'open' : ''}`}
                                                    ></span>
                                                    {openDropdown === 'width' && (
                                                        <div className="dropdown-modal">
                                                            <div className="dropdown-options">
                                                                {widthOptions && widthOptions.length > 0 ? (
                                                                    widthOptions.map((option, index) => (
                                                                        <div 
                                                                            key={index}
                                                                            className="dropdown-option"
                                                                            onClick={(e) => handleSelect('width', option, {}, e)}
                                                                        >
                                                                            {option} mm
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <div className="dropdown-option" style={{ color: 'var(--color-body)', cursor: 'default' }}>
                                                                        No options available
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div> */}
                                            
                                            {/* Plate thickness - Commented out */}
                                            {/* <div className="detail-item">
                                                <div className="detail-label">
                                                    Plate thickness
                                                </div>
                                                <div 
                                                    className="detail-value dropdown-wrapper"
                                                    onClick={(e) => handleDropdownToggle('thickness', e)}
                                                    style={{ cursor: 'pointer', position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                >
                                                    <span>{selectedItem.thickness || 'Select'} mm</span>
                                                    <span 
                                                        className={`dropdown-arrow ${openDropdown === 'thickness' ? 'open' : ''}`}
                                                    ></span>
                                                    {openDropdown === 'thickness' && (
                                                        <div className="dropdown-modal">
                                                            <div className="dropdown-options">
                                                                {thicknessOptions && thicknessOptions.length > 0 ? (
                                                                    thicknessOptions.map((option, index) => (
                                                                        <div 
                                                                            key={index}
                                                                            className="dropdown-option"
                                                                            onClick={(e) => handleSelect('thickness', option, {}, e)}
                                                                        >
                                                                            {option} mm
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <div className="dropdown-option" style={{ color: 'var(--color-body)', cursor: 'default' }}>
                                                                        No options available
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div> */}
                                            
                                            {/* Maintain electrical functions - Commented out */}
                                            {/* <div className="detail-item">
                                                <div className="detail-label">Maintain electrical functions</div>
                                                <div className="detail-value">{productData.specifications?.maintainElectricalFunctions || '-'}</div>
                                            </div> */}
                                            
                                            {/* Surface - Commented out */}
                                            {/* <div className="detail-item">
                                                <div className="detail-label">Surface</div>
                                                <div className="detail-value">{productData.specifications?.surface || '-'}</div>
                                            </div> */}
                                            
                                            {/* Material - Commented out */}
                                            {/* <div className="detail-item">
                                                <div className="detail-label">Material</div>
                                                <div className="detail-value">{productData.specifications?.material || '-'}</div>
                                            </div> */}
                                            
                                            {/* Specifications (Product Summary) - Dynamic Fields */}
                                            {specsArray.map((spec, specIndex) => {
                                                if (!spec.fieldName || !spec.fieldLabel) return null;
                                                const label = spec.fieldLabel;
                                                const value = spec.fieldValue || '-';
                                                return (
                                                    <div key={spec.fieldName || specIndex} className="detail-item">
                                                        <div className="detail-label">{label}</div>
                                                        <div className="detail-value">{value}</div>
                                                    </div>
                                                );
                                            })}
                                            
                                            {/* Custom Fields with Dropdown Options */}
                                            {customFields.map((field, fieldIndex) => {
                                                if (!field.fieldName || !field.fieldLabel) return null;
                                                const fieldName = `customField-${field.fieldName}`;
                                                const options = field.dropdownOptions || [];
                                                const selectedValue = selectedCustomFields[field.fieldName] || '';
                                                
                                                // If only one option, display as regular value (no dropdown)
                                                if (options.length === 1) {
                                                    return (
                                                        <div key={fieldIndex} className="detail-item">
                                                            <div className="detail-label">
                                                                {field.fieldLabel}
                                                            </div>
                                                            <div className="detail-value">{options[0]}</div>
                                                        </div>
                                                    );
                                                }
                                                
                                                // If multiple options, show as dropdown
                                                if (options.length > 1) {
                                                    return (
                                                        <div key={fieldIndex} className="detail-item">
                                                            <div className="detail-label">
                                                                {field.fieldLabel}
                                                            </div>
                                                            <div 
                                                                className="detail-value dropdown-wrapper"
                                                                onClick={(e) => handleDropdownToggle(fieldName, e)}
                                                                style={{ cursor: 'pointer', position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                            >
                                                                <span>{selectedValue || 'Select'}</span>
                                                                <span 
                                                                    className={`dropdown-arrow ${openDropdown === fieldName ? 'open' : ''}`}
                                                                ></span>
                                                                {openDropdown === fieldName && (
                                                                    <div className="dropdown-modal">
                                                                        <div className="dropdown-options">
                                                                            {options.map((option, optionIndex) => (
                                                                                <div 
                                                                                    key={optionIndex}
                                                                                    className="dropdown-option"
                                                                                    onClick={(e) => handleCustomFieldSelect(field.fieldName, option, e)}
                                                                                >
                                                                                    {option}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                
                                                // If no options, show dash
                                                return (
                                                    <div key={fieldIndex} className="detail-item">
                                                        <div className="detail-label">
                                                            {field.fieldLabel}
                                                        </div>
                                                        <div className="detail-value">-</div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* <div className="add-to-cart-section">
                                            <div className="selling-unit">Smallest selling unit: {productData.sellingUnit} m</div>
                                            <div className="quantity-input-group">
                                                <input 
                                                    type="number" 
                                                    className="quantity-input" 
                                                    value={quantity} 
                                                    onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                                                    min={productData.sellingUnit}
                                                    step={productData.sellingUnit}
                                                />
                                                <button className="add-to-cart-btn">
                                                    + Add
                                                </button>
                                            </div>
                                        </div> */}
                                    </div>
                                </div>
                            </div>

                            {/* Tabs Navigation */}
                            {filteredTabs.length > 0 && (
                                <div className="product-tabs-nav">
                                    {filteredTabs.map(tab => (
                                        <button
                                            key={tab.id}
                                            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                                            onClick={() => setActiveTab(tab.id)}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Tab Content */}
                            <div className="product-tab-content">
                                {/* Description Tab */}
                                {activeTab === 'description' && hasDescription && (
                                    <div className="tab-panel">
                                        <h2 className="section-title">Description</h2>
                                        <p className="description-text">
                                            {productData.description}
                                        </p>
                                    </div>
                                )}

                                {/* Technical Data Tab */}
                                {activeTab === 'technical' && hasTechnicalData && (
                                    <div className="tab-panel">
                                        <h2 className="section-title">Technical data</h2>
                                        
                                        <div className="technical-data-section">
                                            <h3 className="subsection-title">Dimensions</h3>
                                            {dimsArray.length > 0 ? (
                                                <div className="data-table">
                                                    {dimsArray.map((dim, index) => {
                                                        const label = dim.fieldLabel || dim.fieldName || `Dimension ${index + 1}`;
                                                        const value = dim.fieldValue || '-';
                                                        const hasUnit = value && !isNaN(value) && (label.toLowerCase().includes('length') || label.toLowerCase().includes('width') || label.toLowerCase().includes('height') || label.toLowerCase().includes('dimension'));
                                                        return (
                                                            <div key={dim.fieldName || index} className="data-row">
                                                                <div className="data-label">{label}</div>
                                                                <div className="data-value">{value} {hasUnit ? 'mm' : ''}</div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-body)' }}>
                                                    No dimensions available.
                                                </div>
                                            )}
                                        </div>

                                        <div className="technical-data-section">
                                            <h3 className="subsection-title">Technical data</h3>
                                            {techArray.length > 0 ? (
                                                <div className="data-table">
                                                    {techArray.map((tech, index) => {
                                                        const label = tech.fieldLabel || tech.fieldName || `Technical Data ${index + 1}`;
                                                        const value = tech.fieldValue || '-';
                                                        const hasUnit = value && !isNaN(value) && label.toLowerCase().includes('thickness');
                                                        return (
                                                            <div key={tech.fieldName || index} className="data-row">
                                                                <div className="data-label">{label}</div>
                                                                <div className="data-value">{value} {hasUnit ? 'mm' : ''}</div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-body)' }}>
                                                    No technical data available.
                                                </div>
                                            )}
                                        </div>

                                        {productData.loads && productData.loads.length > 0 && (
                                            <div className="technical-data-section">
                                                <h3 className="subsection-title">Loads</h3>
                                                <div className="loads-data">
                                                    {productData.loads.map((load, index) => (
                                                        <div key={index} className="load-item">
                                                            <span className="load-spacing">Support spacing: {load.spacing} m</span>
                                                            <span className="load-value">{load.value} kN/m</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Downloads Tab */}
                                {activeTab === 'downloads' && hasDownloads && (
                                    <div className="tab-panel">
                                        <h2 className="section-title">Downloads</h2>
                                        <div className="downloads-list">
                                            {downloadItems.datasheet && downloadItems.datasheet.length > 0 && (
                                                <div className="download-category">
                                                    <div 
                                                        className="category-header"
                                                        onClick={() => handleDownloadCategoryToggle('datasheet')}
                                                    >
                                                        <span className="category-name">
                                                            Data sheet ({downloadItems.datasheet.length})
                                                        </span>
                                                        <span className={`dropdown-arrow ${openDownloadCategory === 'datasheet' ? 'open' : ''}`}></span>
                                                    </div>
                                                    {openDownloadCategory === 'datasheet' && (
                                                        <div className="download-items">
                                                            {downloadItems.datasheet.map((item, index) => {
                                                                const downloadUrl = item.url?.startsWith('/uploads/') 
                                                                    ? `${ASSET_BASE}${item.url}` 
                                                                    : item.url || '#';
                                                                const downloadId = `datasheet-${index}`;
                                                                return (
                                                                    <div key={index} className="download-item">
                                                                        <label className="download-checkbox">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedDownloads.includes(downloadId)}
                                                                                onChange={() => handleDownloadToggle('datasheet', index)}
                                                                            />
                                                                            <span className="download-name">{item.name || 'Datasheet'}</span>
                                                                            {item.size && <span className="download-size">({item.size})</span>}
                                                                        </label>
                                                                        <a 
                                                                            href={downloadUrl} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="download-link-btn"
                                                                        >
                                                                            Download
                                                                        </a>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {downloadItems.catalogues && downloadItems.catalogues.length > 0 && (
                                                <div className="download-category">
                                                    <div 
                                                        className="category-header"
                                                        onClick={() => handleDownloadCategoryToggle('catalogues')}
                                                    >
                                                        <span className="category-name">
                                                            Catalogues & Flyers ({downloadItems.catalogues.length})
                                                        </span>
                                                        <span className={`dropdown-arrow ${openDownloadCategory === 'catalogues' ? 'open' : ''}`}></span>
                                                    </div>
                                                    {openDownloadCategory === 'catalogues' && (
                                                        <div className="download-items">
                                                            {downloadItems.catalogues.map((item, index) => {
                                                                const downloadUrl = item.url?.startsWith('/uploads/') 
                                                                    ? `${ASSET_BASE}${item.url}` 
                                                                    : item.url || '#';
                                                                const downloadId = `catalogues-${index}`;
                                                                return (
                                                                    <div key={index} className="download-item">
                                                                        <label className="download-checkbox">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedDownloads.includes(downloadId)}
                                                                                onChange={() => handleDownloadToggle('catalogues', index)}
                                                                            />
                                                                            <span className="download-name">{item.name || 'Catalogue'}</span>
                                                                            {item.size && <span className="download-size">({item.size})</span>}
                                                                        </label>
                                                                        <a 
                                                                            href={downloadUrl} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="download-link-btn"
                                                                        >
                                                                            Download
                                                                        </a>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {downloadItems.instructions && downloadItems.instructions.length > 0 && (
                                                <div className="download-category">
                                                    <div 
                                                        className="category-header"
                                                        onClick={() => handleDownloadCategoryToggle('instructions')}
                                                    >
                                                        <span className="category-name">
                                                            Instructions and Aid ({downloadItems.instructions.length})
                                                        </span>
                                                        <span className={`dropdown-arrow ${openDownloadCategory === 'instructions' ? 'open' : ''}`}></span>
                                                    </div>
                                                    {openDownloadCategory === 'instructions' && (
                                                        <div className="download-items">
                                                            {downloadItems.instructions.map((item, index) => {
                                                                const downloadUrl = item.url?.startsWith('/uploads/') 
                                                                    ? `${ASSET_BASE}${item.url}` 
                                                                    : item.url || '#';
                                                                const downloadId = `instructions-${index}`;
                                                                return (
                                                                    <div key={index} className="download-item">
                                                                        <label className="download-checkbox">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedDownloads.includes(downloadId)}
                                                                                onChange={() => handleDownloadToggle('instructions', index)}
                                                                            />
                                                                            <span className="download-name">{item.name || 'Instruction'}</span>
                                                                            {item.size && <span className="download-size">({item.size})</span>}
                                                                        </label>
                                                                        <a 
                                                                            href={downloadUrl} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="download-link-btn"
                                                                        >
                                                                            Download
                                                                        </a>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {downloadItems.images && downloadItems.images.length > 0 && (
                                                <div className="download-category">
                                                    <div 
                                                        className="category-header"
                                                        onClick={() => handleDownloadCategoryToggle('images')}
                                                    >
                                                        <span className="category-name">
                                                            Product images ({downloadItems.images.length})
                                                        </span>
                                                        <span className={`dropdown-arrow ${openDownloadCategory === 'images' ? 'open' : ''}`}></span>
                                                    </div>
                                                    {openDownloadCategory === 'images' && (
                                                        <div className="download-items">
                                                            {downloadItems.images.map((item, index) => {
                                                                const downloadUrl = item.url?.startsWith('/uploads/') 
                                                                    ? `${ASSET_BASE}${item.url}` 
                                                                    : item.url || '#';
                                                                const downloadId = `images-${index}`;
                                                                return (
                                                                    <div key={index} className="download-item">
                                                                        <label className="download-checkbox">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedDownloads.includes(downloadId)}
                                                                                onChange={() => handleDownloadToggle('images', index)}
                                                                            />
                                                                            <span className="download-name">{item.name || 'Image'}</span>
                                                                            {item.size && <span className="download-size">({item.size})</span>}
                                                                        </label>
                                                                        <a 
                                                                            href={downloadUrl} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="download-link-btn"
                                                                        >
                                                                            Download
                                                                        </a>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {downloadItems.tender && downloadItems.tender.length > 0 && (
                                                <div className="download-category">
                                                    <div 
                                                        className="category-header"
                                                        onClick={() => handleDownloadCategoryToggle('tender')}
                                                    >
                                                        <span className="category-name">
                                                            Tender specifications ({downloadItems.tender.length})
                                                        </span>
                                                        <span className={`dropdown-arrow ${openDownloadCategory === 'tender' ? 'open' : ''}`}></span>
                                                    </div>
                                                    {openDownloadCategory === 'tender' && (
                                                        <div className="download-items">
                                                            {downloadItems.tender.map((item, index) => {
                                                                const downloadUrl = item.url?.startsWith('/uploads/') 
                                                                    ? `${ASSET_BASE}${item.url}` 
                                                                    : item.url || '#';
                                                                const downloadId = `tender-${index}`;
                                                                return (
                                                                    <div key={index} className="download-item">
                                                                        <label className="download-checkbox">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedDownloads.includes(downloadId)}
                                                                                onChange={() => handleDownloadToggle('tender', index)}
                                                                            />
                                                                            <span className="download-name">{item.name || 'Tender'}</span>
                                                                            {item.size && <span className="download-size">({item.size})</span>}
                                                                        </label>
                                                                        <a 
                                                                            href={downloadUrl} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="download-link-btn"
                                                                        >
                                                                            Download
                                                                        </a>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {(!downloadItems.datasheet || downloadItems.datasheet.length === 0) &&
                                             (!downloadItems.catalogues || downloadItems.catalogues.length === 0) &&
                                             (!downloadItems.instructions || downloadItems.instructions.length === 0) &&
                                             (!downloadItems.images || downloadItems.images.length === 0) &&
                                             (!downloadItems.tender || downloadItems.tender.length === 0) && (
                                                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-body)' }}>
                                                    No downloads available for this product.
                                                </div>
                                            )}
                                        </div>
                                        {selectedDownloads.length > 0 && (
                                            <div className="download-selection">
                                                <button 
                                                    className="download-selection-btn"
                                                    onClick={handleDownloadSelection}
                                                >
                                                    Download selection ({selectedDownloads.length})
                                                </button>
                                                <button 
                                                    className="download-clear-btn"
                                                    onClick={() => setSelectedDownloads([])}
                                                >
                                                    Clear selection
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Variants Tab */}
                                {activeTab === 'variants' && hasVariants && (
                                    <div className="tab-panel">
                                        <h2 className="section-title">Variants</h2>
                                        {variants.length > 0 ? (
                                            <div className="variants-table-wrapper">
                                                <table className="variants-table">
                                                    <thead>
                                                        <tr>
                                                            {productData.variantColumns && productData.variantColumns.length > 0 ? (
                                                                productData.variantColumns.map((col, idx) => (
                                                                    <th key={idx}>{col.fieldLabel || col.fieldName}</th>
                                                                ))
                                                            ) : (
                                                                variants[0] && Object.keys(variants[0]).map((key, idx) => (
                                                                    <th key={idx}>{key}</th>
                                                                ))
                                                            )}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {variants.map((variant, index) => (
                                                            <tr key={index}>
                                                                {productData.variantColumns && productData.variantColumns.length > 0 ? (
                                                                    productData.variantColumns.map((col, idx) => (
                                                                        <td key={idx}>{variant[col.fieldName] || ''}</td>
                                                                    ))
                                                                ) : (
                                                                    Object.values(variant).map((value, idx) => (
                                                                        <td key={idx}>{String(value)}</td>
                                                                    ))
                                                                )}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-body)' }}>
                                                No variants available.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    )
}

export default Products;

